import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '@/lib/axios';
import { buildBusinessDirectoryParams } from '@/lib/business-directory-params';
import type {
  BusinessDirectoryFiltersState,
  BusinessDirectorySort,
  CommunityBusiness,
} from '@/types';

const PAGE_SIZE = 50;

export interface BusinessDirectoryMetadata {
  totalCount: number;
  totalPages: number;
  currentPage: number;
  limit: number;
  hasMore: boolean;
}

interface BusinessDirectoryResponse {
  businesses: CommunityBusiness[];
  metadata: BusinessDirectoryMetadata;
}

export function useBusinessDirectory(
  communityId?: string,
  search = '',
  filters: BusinessDirectoryFiltersState = {},
  sort: BusinessDirectorySort = 'business'
) {
  const [businesses, setBusinesses] = useState<CommunityBusiness[]>([]);
  const [metadata, setMetadata] = useState<BusinessDirectoryMetadata | null>(null);
  const [page, setPage] = useState(1);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const fetchIdRef = useRef(0);
  const businessesRef = useRef<CommunityBusiness[]>([]);

  businessesRef.current = businesses;

  const filterKey = JSON.stringify({
    category: filters.category || '',
    city: filters.city || '',
    state: filters.state || '',
    country: filters.country || '',
  });

  const hasMore = metadata?.hasMore ?? false;

  const fetchPage = useCallback(
    async (targetPage: number, reset: boolean) => {
      if (!communityId) return;

      const fetchId = ++fetchIdRef.current;

      if (reset) {
        if (businessesRef.current.length === 0) {
          setIsInitialLoading(true);
        } else {
          setIsRefreshing(true);
        }
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      try {
        const params = buildBusinessDirectoryParams(search, filters, targetPage, PAGE_SIZE, sort);

        const { data } = await axiosInstance.get<BusinessDirectoryResponse>(
          `/api/communities/${communityId}/business?${params.toString()}`
        );

        if (fetchId !== fetchIdRef.current) return;

        setMetadata(data.metadata);
        setPage(targetPage);
        setBusinesses((prev) => (reset ? data.businesses : [...prev, ...data.businesses]));
      } catch (err) {
        if (fetchId !== fetchIdRef.current) return;
        setError(err);
      } finally {
        if (fetchId === fetchIdRef.current) {
          setIsInitialLoading(false);
          setIsRefreshing(false);
          setIsLoadingMore(false);
        }
      }
    },
    [communityId, search, filterKey, filters, sort]
  );

  useEffect(() => {
    if (!communityId) {
      setBusinesses([]);
      setMetadata(null);
      setPage(1);
      setIsInitialLoading(false);
      setIsRefreshing(false);
      return;
    }

    void fetchPage(1, true);
  }, [communityId, search, filterKey, sort, fetchPage]);

  const loadMore = useCallback(() => {
    if (!communityId || isInitialLoading || isRefreshing || isLoadingMore || !hasMore) return;
    void fetchPage(page + 1, false);
  }, [communityId, fetchPage, hasMore, isInitialLoading, isRefreshing, isLoadingMore, page]);

  return {
    businesses,
    metadata,
    isInitialLoading,
    isRefreshing,
    isLoadingMore,
    isError: error,
    hasMore,
    loadMore,
  };
}

export async function fetchAllBusinessDirectory(
  communityId: string,
  search = '',
  filters: BusinessDirectoryFiltersState = {},
  sort: BusinessDirectorySort = 'business'
): Promise<CommunityBusiness[]> {
  const all: CommunityBusiness[] = [];
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const params = buildBusinessDirectoryParams(search, filters, page, PAGE_SIZE, sort);
    const { data } = await axiosInstance.get<BusinessDirectoryResponse>(
      `/api/communities/${communityId}/business?${params.toString()}`
    );
    all.push(...data.businesses);
    hasMore = Boolean(data.metadata.hasMore);
    page += 1;
    if (page > 200) break;
  }

  return all;
}
